"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useMotionMode } from "@/context/MotionContext";
import { getScrimColor } from "@/utils/colors";
import { BlogFeed } from "@/components/ui/BlogFeed";
import { Footer } from "@/components/sections/Footer";
import { BLOG_POSTS } from "@/data/blog";
import { TopScrim } from "@/components/ui/TopScrim";

// =============================================================================
// BLOG OVERLAY
// =============================================================================
// Single orb domain. Landing card below the orb, scroll to enter blog content.
// Follows ManifestoOverlay pattern: fixed HTML layer at z-14 with manual scroll.

const BLOG_FEED_ENABLED = true;

interface BlogOverlayProps {
  isActive: boolean;
  z1Height: number;
  z2Height: number;
  crtEnabled?: boolean;
  isLightMode?: boolean;
  onDeepScrollChange?: (isDeep: boolean) => void;
  // Fires on any scroll past ~20px. Separate from deep-scroll: lets the hero
  // dim early (before pills would collide), while deep-scroll still gates the
  // orb pause + audio cue.
  onScrolledChange?: (scrolled: boolean) => void;
  onLightboxChange?: (isOpen: boolean) => void;
  initialPostId?: string | null;
}

export function BlogOverlay({ isActive, z1Height, z2Height, crtEnabled = true, isLightMode = false, onDeepScrollChange, onScrolledChange, onLightboxChange, initialPostId }: BlogOverlayProps) {
  const scrimColor = getScrimColor(isLightMode);
  const { canAnimate } = useMotionMode();
  const [isVisible, setIsVisible] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Spacer height: positions pills at ~50vh on initial load. Upper half is
  // orb space; lower half is where feed starts. Formula derived from
  // z1+z2+z3=viewportHeight, z3≈28px idle.
  const spacerHeight = Math.max(16, Math.round(z2Height * 0.5 - z1Height * 0.5 + 10));

  // Track deep scroll state ref to avoid stale closures in scroll handler
  const isDeepScrollRef = useRef(false);

  const reportDeepScroll = useCallback((isDeep: boolean) => {
    if (isDeepScrollRef.current === isDeep) return;
    isDeepScrollRef.current = isDeep;
    onDeepScrollChange?.(isDeep);
  }, [onDeepScrollChange]);

  // Propagate any-scroll state upward so the hero can dim before deep-scroll
  // fires. Scoped to active state so leaving the domain resets cleanly.
  useEffect(() => {
    onScrolledChange?.(isActive ? isScrolled : false);
  }, [isScrolled, isActive, onScrolledChange]);

  // Handle visibility with fade timing
  useEffect(() => {
    if (isActive) {
      setIsVisible(true);
      const timer = setTimeout(() => setShowContent(true), 200);
      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
      reportDeepScroll(false); // Reset pause state on exit
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsScrolled(false);
        setIsAtBottom(false);
        if (containerRef.current) {
          containerRef.current.scrollTop = 0;
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isActive, reportDeepScroll]);

  // Manual scroll handling (prevents Lenis from intercepting)
  useEffect(() => {
    if (!isActive || !isVisible || !containerRef.current) return;
    const c = containerRef.current;

    const checkScrollState = () => {
      setIsScrolled(c.scrollTop > 20);
      setIsAtBottom(c.scrollTop + c.clientHeight >= c.scrollHeight - 50);
      // Deep scroll = orb spacer fully scrolled away — user is browsing card grid
      reportDeepScroll(c.scrollTop > spacerHeight);
    };

    // If the expand dialog (or any other modal) is open, let it handle its own
    // wheel/keyboard scroll. Don't hijack events into the feed container.
    const isDialogOpen = () => !!document.querySelector('[role="dialog"]');

    const onWheel = (e: WheelEvent) => {
      if (isDialogOpen()) return;
      const rect = c.getBoundingClientRect();
      if (e.clientX >= rect.left && e.clientX <= rect.right &&
          e.clientY >= rect.top && e.clientY <= rect.bottom) {
        e.preventDefault();
        c.scrollTop += e.deltaY;
        checkScrollState();
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (isDialogOpen()) return;
      const scrollAmount = 80;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        c.scrollTop += scrollAmount;
        checkScrollState();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        c.scrollTop -= scrollAmount;
        checkScrollState();
      }
    };

    const onScroll = () => checkScrollState();

    checkScrollState();
    window.addEventListener("wheel", onWheel, { passive: false });
    c.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("wheel", onWheel);
      c.removeEventListener("scroll", onScroll);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isActive, isVisible, spacerHeight, reportDeepScroll]);

  if (!isVisible) return null;

  return (
    <>
      <TopScrim isActive={isActive} isScrolled={isScrolled} z1Height={z1Height} isLightMode={isLightMode} />

      {/* Bottom scrim */}
      <div
        className="fixed left-0 right-0 z-[15] pointer-events-none"
        style={{
          bottom: 0,
          height: 200,
          background: `linear-gradient(to top, rgba(${scrimColor},1) 0%, rgba(${scrimColor},0.8) 30%, rgba(${scrimColor},0) 100%)`,
          opacity: isActive && !isAtBottom ? 1 : 0,
          transition: canAnimate ? `opacity ${isActive ? 400 : 150}ms ease-out` : "none",
        }}
      />

      {/* Scrollable blog container — pointer-events:auto when active so the
          sticky filter bar always receives clicks. The spacer is
          pointer-events:none so clicks in the ring area pass through to orbs.
          When inactive, the whole container is pointer-events:none. */}
      <div
        ref={containerRef}
        data-blog
        className="fixed left-0 right-0 z-[14] overscroll-contain"
        style={{
          pointerEvents: isActive ? "auto" : "none",
          top: z1Height,
          height: `calc(100vh - ${z1Height}px)`,
          overflowY: "scroll",
          // Top fade used to be 15% (~120px) to smooth card edges against the
          // hero band. Now that the hero dims to 0.18 when scrolled, the sharp
          // edge is already softened — we can shrink the top fade to a tiny
          // hairline so the sticky pill bar (which sits ~44px down) isn't
          // mask-faded into near-invisibility.
          maskImage: "linear-gradient(to bottom, transparent 0%, black 8px, black 85%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 8px, black 85%, transparent 100%)",
          opacity: isActive ? 1 : 0,
          transition: canAnimate ? `opacity ${isActive ? 400 : 150}ms ease-out` : "none",
        }}
      >
        {/* Spacer -- positions filter pills at ~50vh on initial load.
            Upper half = ring/orb space; lower half = where feed starts.
            Also serves as the deep-scroll threshold: scrollTop > spacerHeight = orb gone.
            pointer-events:none so clicks pass through to orbs beneath. */}
        <div style={{ height: spacerHeight, pointerEvents: "none" }} />

        {/* Feed (dev) or placeholder (prod) */}
        {BLOG_FEED_ENABLED ? (
          <div
            style={{
              opacity: showContent ? 1 : 0,
              transform: showContent ? "translateY(0)" : "translateY(30px)",
              transition: canAnimate
                ? "opacity 600ms ease-out 600ms, transform 800ms cubic-bezier(0.16, 1, 0.3, 1) 600ms"
                : "none",
            }}
          >
            <BlogFeed posts={BLOG_POSTS.filter(p => !p.draft)} crtEnabled={crtEnabled} isLightMode={isLightMode} onLightboxChange={onLightboxChange} initialPostId={initialPostId} />
            <div className="px-3 sm:px-6 pb-12">
              <Footer variant="cockpit" />
            </div>
          </div>
        ) : (
          <div className="pb-32" />
        )}
      </div>
    </>
  );
}
